import { UseQueryOptions } from '@tanstack/react-query';
import { Interval } from '@ui/constants/poll';
import { OpenMarketTradeParams } from '@ui/features/Trade/types';
import { useDataChainId } from '@ui/hooks/datachain';
import { useQueryOnce } from '@ui/hooks/useQueryOnce';
import { toReadableValue } from '@ui/lib/bignumber';
import { isNil, omit } from 'lodash-es';
import { useDeferredValue, useEffect, useMemo } from 'react';
import { useAccount, usePublicClient } from 'wagmi';

import { getTradingPortalFacet } from '../../contract-instances/getTradingPortalFacet';

export const useEstimateOpenPositionGasLimit = <TSelect = BigInt>({
  openMarketTradeParams,
  opts,
}: {
  openMarketTradeParams: OpenMarketTradeParams | undefined;
  opts?: Pick<UseQueryOptions<BigInt, Error, TSelect>, 'enabled'>;
}) => {
  const { address } = useAccount();
  const chainId = useDataChainId();
  const client = usePublicClient({
    chainId,
  });

  const openMarketTradeParamsSnapshot = useDeferredValue(openMarketTradeParams);

  const TradingPortalFacet = useMemo(() => {
    if (!client) return;

    return getTradingPortalFacet({
      chainId,
      publicClient: client,
    });
  }, [chainId, client]);

  return useQueryOnce({
    meta: {
      type: 'estimateGas',
    },
    queryKey: [
      `TradingPortalFacet.estimate.openMarketTradeETH/openMarketTrade`,
      address,
      chainId,
      client?.uid,
    ],
    refetchInterval: Interval.FAST,
    queryFn: async () => {
      if (isNil(openMarketTradeParamsSnapshot) || isNil(address))
        return undefined;
      const omitValue = omit(openMarketTradeParamsSnapshot, [
        'value',
        'pythUpdateData',
        'oracleUpdateData',
        'extraFee',
      ]);

      const re = await TradingPortalFacet?.estimateGas.openMarketTradeV2(
        [
          omitValue,
          openMarketTradeParamsSnapshot.oracleUpdateData,
          openMarketTradeParamsSnapshot.extraFee,
        ],
        {
          account: address,
          value: openMarketTradeParamsSnapshot.value,
        },
      );

      if (isNil(re)) return undefined;

      return toReadableValue(re);
    },
    enabled:
      !!address &&
      !!openMarketTradeParamsSnapshot &&
      !!TradingPortalFacet &&
      (opts?.enabled ?? true),
  });
};
